import type { AxiosInstance } from 'axios';
import { inRange } from 'lodash-es';
import { err, ok } from 'neverthrow';
import defaultClient from './defaultClient';
import type { IParams } from './IParams';
import { Api } from './Api';
import { DataProviderError } from './dataProvider/DataProviderError';
import type { DataProviderResult } from './dataProvider/DataProviderResult';
import type { IDataProvider } from './dataProvider/IDataProvider';
import type { IListOptions } from './dataProvider/IListOptions';
import type { IListResult } from './dataProvider/IListResult';
import { ValidationError } from './ValidationError';

export class RestApi<T> extends Api implements IDataProvider<T> {
	constructor(
		public resourcePath: string,
		client: AxiosInstance = defaultClient
	) {
		super(client);
	}

	async get(id: number | string): Promise<DataProviderResult<T>> {
		const response = await this.client.get(`${this.resourcePath}/${id}`);

		if (this.isValidResponseStatus(response.status)) {
			return ok(response.data['data']);
		}

		return err(new DataProviderError(`unexpected status (${response.status})`, response.status));
	}

	async list(params: IListOptions): Promise<DataProviderResult<IListResult<T>>> {
		const response = await this.client.get(this.resourcePath, { params });

		if (this.isValidResponseStatus(response.status)) {
			return ok(response.data);
		}

		return err(new DataProviderError(`unexpected status (${response.status})`, response.status));
	}

	async options(
		attributeName: string,
		params: IListOptions
	): Promise<DataProviderResult<IListResult<string>>> {
		const response = await this.client.get(`${this.resourcePath}/options/${attributeName}`, {
			params
		});

		if (this.isValidResponseStatus(response.status)) {
			return ok(response.data);
		}

		return err(new DataProviderError(`unexpected status (${response.status})`, response.status));
	}

	async create(params: IParams): Promise<DataProviderResult<T>> {
		const response = await this.client.post(this.resourcePath, params);

		if (this.isValidResponseStatus(response.status)) {
			return ok(response.data['data']);
		} else if (response.status === 422) {
			return err(new ValidationError(response.data['errors']));
		}

		return err(new DataProviderError(`unexpected status (${response.status})`, response.status));
	}

	async update(id: number | string, params: IParams): Promise<DataProviderResult<T>> {
		const response = await this.client.put(`${this.resourcePath}/${id}`, params);

		if (this.isValidResponseStatus(response.status)) {
			return ok(response.data['data']);
		} else if (response.status === 422) {
			return err(new ValidationError(response.data['errors']));
		}

		return err(new DataProviderError(`unexpected status (${response.status})`, response.status));
	}

	async batchUpdate(ids: number[] | string[], params: IParams): Promise<DataProviderResult<T>> {
		const response = await this.client.put(
			`${this.resourcePath}`,
			{
				ids: ids,
				...params
			},
			{
				timeout: 300000 // sets 5 min timemout in case of a big data count
			}
		);

		if (this.isValidResponseStatus(response.status)) {
			return ok(response.data['data']);
		} else if (response.status === 422) {
			return err(new ValidationError(response.data['errors']));
		}

		return err(new DataProviderError(`unexpected status (${response.status})`, response.status));
	}

	async delete(id: number | string): Promise<DataProviderResult<void>> {
		const response = await this.client.delete(`${this.resourcePath}/${id}`);

		if (this.isValidResponseStatus(response.status)) {
			return ok(undefined);
		}

		return err(new DataProviderError(`unexpected status (${response.status})`, response.status));
	}

	async batchDelete(ids: number[] | string[]): Promise<DataProviderResult<void>> {
		const response = await this.client.delete(`${this.resourcePath}`, {
			data: {
				ids: ids
			},
			timeout: 300000 // sets 5 min timemout in case of a big data count
		});

		if (this.isValidResponseStatus(response.status)) {
			return ok(undefined);
		}

		return err(new DataProviderError(`unexpected status (${response.status})`, response.status));
	}

	async action(id: number | string, action: string): Promise<DataProviderResult<void>> {
		const response = await this.client.put(`${this.resourcePath}/${id}/${action}`);

		if (this.isValidResponseStatus(response.status)) {
			return ok(undefined);
		}

		return err(new DataProviderError(`unexpected status (${response.status})`, response.status));
	}

	async batchAction(ids: number[] | string[], action: string): Promise<DataProviderResult<void>> {
		const response = await this.client.put(
			`${this.resourcePath}/${action}`,
			{
				ids: ids
			},
			{
				timeout: 300000 // sets 5 min timemout in case of a big data count
			}
		);

		if (this.isValidResponseStatus(response.status)) {
			return ok(undefined);
		}

		return err(new DataProviderError(`unexpected status (${response.status})`, response.status));
	}

	protected isValidResponseStatus(status: number): boolean {
		return inRange(status, 200, 300);
	}
}
